import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./style.css";
import { Download, DownloadIcon, Search } from "../../constants";
import { EmptyList } from "../empty";
import { downloadYearWiseReport } from "../../helper-functions";
import { get } from "../../config/api";
import { API_ENDPOINTS } from "../../constants/apiEndpoints";
import { Loader } from "../loader";
import { useDispatch, useSelector } from "react-redux";
import { ROLES } from "../../constants/textconstants";
import { formatDateToDDMMYYYY } from "../../utils/dateUtils";
import { showError } from "../../store/slices/formconfig";
import { TESTING_DATA } from "../../constants/isTesting";
import { setIssuedCertificatesPage } from "../../store/slices/pendingRequests";

interface Certificate {
  retailer: string;
  brpId: string;
  territory: string;
  certificateNo: string;
  issueDate: string;
  status: string;
}

interface Props {
  onYearChange?: (year: string) => void;
  showHeaderActions?: boolean;
  from?: string;
  bookletId?: string;
}

const IssuedCertificates: React.FC<Props> = ({
  onYearChange,
  showHeaderActions = true,
  from,
  bookletId,
}) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear.toString());
  const years = Array.from({ length: currentYear - 2025 + 1 }, (_, i) => 2025 + i);
  const [isDownloading, setIsDownloading] = useState(false);
  const [certificates, setCertificates] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const savedPage = useSelector((state: any) => state.requests.issuedCertificatesPage) || 1;
  const [pagination, setPagination] = useState({ page: savedPage, size: 10, total: 0 });
  const userData = useSelector((state: any) => state.formConfig.user);
  const dispatch = useDispatch();

  const fetchData = async (overridePage?: number) => {
    setIsLoading(true);
    const page = overridePage ?? pagination.page;
    try {
      const params = new URLSearchParams({
        status: "COMPLETED",
        page: page.toString(),
        size: pagination.size.toString(),
      });
      if (debouncedSearchQuery) {
        params.append("searchBy", debouncedSearchQuery);
      }
      if (userData?.role) {
        params.append("userRole", userData.role);
      }
      if (from !== "assigned") {
        let response: any = {};
        if (userData.is_viewed_guided_tour) {
          response = await get(
            `${API_ENDPOINTS.CERTIFICATE_REQUESTS_BY_USER(
              userData.userId
            )}?${params.toString()}`
          );
        } else {
          response = TESTING_DATA.ISSUED_SERTIFICATES
        }

        if (response && response.items) {
          setCertificates(response.items);
          setPagination({
            page: response.page,
            size: response.size,
            total: response.total,
          });
        } else {
          setCertificates([]);
          setPagination({ page: 1, size: 10, total: 0 });
        }
      } else {
        const response = await get(
          `${API_ENDPOINTS.CERTIFICATE_REQUESTS_BY_BOOKLET_ID(bookletId)}`
        );
        if (response && response.length > 0) {
          setCertificates(response);
          // setPagination({
          //   page: response.page,
          //   size: response.size,
          //   total: response.total,
          // });
        } else {
          setCertificates([]);
          setPagination({ page: 1, size: 10, total: 0 });
        }
      }
    } catch (error) {
      dispatch(showError({ message: `Failed to fetch issued certificates, due to some technical issue, please try again`, visible: true }))

      console.error("Failed to fetch issued certificates:", error);
      setCertificates([]);
      setPagination({ page: 1, size: 10, total: 0 });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchQuery !== debouncedSearchQuery) {
        setDebouncedSearchQuery(searchQuery);
        setPagination((p) => ({ ...p, page: 1 }));
        dispatch(setIssuedCertificatesPage(1));
        fetchData(1);
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  useEffect(() => {
    fetchData(savedPage);
  }, [userData.userId]);

  // useEffect(()=>{
  //    fetchData();
  // },[])

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setYear(e.target.value);
    if (onYearChange) {
      onYearChange(e.target.value);
    }
  };

  const handleDownloadReport = async () => {
    setIsDownloading(true);
    try {
      await downloadYearWiseReport(year);
    } finally {
      setIsDownloading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPagination((prev) => ({ ...prev, page: newPage }));
      dispatch(setIssuedCertificatesPage(newPage));
      fetchData(newPage);
    }
  };

  const totalPages = Math.ceil(pagination.total / pagination.size);

  return (
    <div className="issued-certificates">
      <div className="issued-certificates-header">
        <h3>Issued Certificates</h3>

        {!showHeaderActions && (
          <button
            onClick={() => {
              if (userData.role === ROLES.ISSUER) {
                navigate("/all-requests/2");
              } else {
                navigate("/issued-certificates");
              }
            }}
            className="viewAllButton"
          >
            View All
          </button>
        )}

        {showHeaderActions && from !== "assigned" && (
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            {isDownloading ? (
              <div className="download-spinner">Downloading...</div>
            ) : (
              <div
                onClick={handleDownloadReport}
                style={{
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <img src={DownloadIcon} style={{ height: 20 }} />
                <span className="downloadfull">Download Full Report</span>
              </div>
            )}

            <div className="year-selector">
              <select value={year} onChange={handleYearChange}>
                {years.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {showHeaderActions && (
        <div className="issued-certificates-actions">
          <input
            type="text"
            placeholder="Search by firm name or BRP ID"
            style={{
              backgroundImage: `url(${Search})`,
              backgroundSize: 20,
              backgroundPosition: "9px 11px",
              backgroundRepeat: "no-repeat",
              paddingLeft: 40,
            }}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
      )}
      {isLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: 50 }}>
          <Loader />
        </div>
      ) : certificates.length === 0 ? (
        <EmptyList from="issued" />
      ) : (
        <table>
          <thead>
            {from !== "assigned" ? (
              <tr>
                <th>Retailer</th>
                <th>Territory</th>
                <th>Certificate No.</th>
                <th>Issue Date</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            ) : (
              <tr>
                <th>Certificate No.</th>
                <th>Firm Name</th>
                <th>BRP ID</th>
                <th>Issue Date</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            )}
          </thead>
          <tbody>
            {from !== "assigned"
              ? certificates
                .slice(0, showHeaderActions ? certificates.length : 3)
                .map((cert: any, index) => (
                  <tr key={index}>
                    <td style={{maxWidth:100, wordBreak:'break-word'}}>
                      {cert.firm_name}
                    </td>
                    <td>{cert.partner_location}</td>
                    <td>
                      {cert.certificate?cert.certificate.certificate_number:'-'}
                    </td>
                    
                    <td>
                      {cert.certificate? formatDateToDDMMYYYY(cert.certificate.issued_date) :
                        "-"}
                    </td>
                    <td>
                      <span className={`status-${cert.status.toLowerCase()}`}>
                        {cert.status.toLowerCase() === "approved"
                          ? "Pending Delivery"
                          : cert.status}
                      </span>
                    </td>
                    <td>
                      <button
                        className={`view-details-button ${index==0 && 'first_action_button'}`}
                        onClick={() =>
                          navigate(`/review-request/${cert.request_id}`)
                        }
                        disabled={!cert.request_id}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))
              : certificates
                .slice(0, showHeaderActions ? certificates.length : 3)
                .map((cert: any, index) => (
                  <tr key={index}>
                    <td>{cert.certificate_number || "-"}</td>
                    <td>
                      {cert.partner && cert.partner.name}
                    </td>
                    <td>{cert.partner.partner_id}</td>

                    <td>
                      {formatDateToDDMMYYYY(cert.issued_date) ||
                        "-"}
                    </td>
                    <td>
                      <span className={`status-${cert.status.toLowerCase()}`}>
                        {cert.status.toLowerCase() === "approved"
                          ? "Pending Delivery"
                          : cert.status}
                      </span>
                    </td>
                    <td>
                      <button
                        className={`view-details-button ${index==0 && 'first_action_button'}`}
                        onClick={() =>
                          navigate(`/review-request/${cert.request_id}`)
                        }
                        disabled={!cert.request_id}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      )}
      {showHeaderActions && totalPages > 1 && (
        <div className="pagination-controls">
          <button
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page === 1}
            style={{background: pagination.page === 1? '#efefef': 'var(--color-primary)' }}
          >
            Previous
          </button>
          <span>
            Page {pagination.page} of {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page >= totalPages}
            style={{background: pagination.page >= totalPages? '#efefef': 'var(--color-primary)' }}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default IssuedCertificates;
